import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Shield, Save, Trash2, Ban, Play, Sparkles, MessageSquare, Send, User as UserIcon,
  Circle, KeyRound, Copy,
} from "lucide-react";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [msgs, setMsgs] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const listRef = useRef(null);

  const load = async () => {
    const r = await api.get(`/admin/users/${id}`);
    setData(r.data);
  };

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (tab === "messages") {
      api.get(`/admin/messages/${id}`).then((r) => setMsgs(r.data));
    }
  }, [tab, id]);

  useEffect(() => {
    if (tab === "messages") listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, tab]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${id}`, patch);
      setData({ ...data, user: r.data });
      toast.success("Aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const suspend = () => save({ status: data.user.status === "suspended" ? "active" : "suspended" });

  const del = async () => {
    if (!window.confirm(`Eliminare ${data.user.name}? Verrà cancellato con tutti i suoi dati.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Cliente eliminato");
      navigate("/admin");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const genPlan = async () => {
    setGenLoading(true);
    toast.info("Genero programma per questo cliente...");
    try {
      await api.post(`/admin/users/${id}/plan`, { weeks: 4 });
      toast.success("Programma generato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore generazione"); }
    finally { setGenLoading(false); }
  };

  const sendMsg = async () => {
    if (!msgInput.trim()) return;
    try {
      const r = await api.post(`/admin/messages/${id}`, { content: msgInput });
      setMsgs([...msgs, r.data]);
      setMsgInput("");
    } catch { toast.error("Errore invio"); }
  };

  if (!data) return <div className="max-w-6xl mx-auto p-8 text-[#A0A0AB]">Caricamento...</div>;

  const { user, profile, measurements, sessions, programs, stats } = data;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/admin" className="text-xs uppercase tracking-widest text-[#A0A0AB] hover:text-white flex items-center gap-1 mb-4" data-testid="back-admin">
        <ArrowLeft className="w-3 h-3" /> Lista clienti
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#E2FF31] mb-2">
            <Shield className="w-3 h-3" /> Cliente
            {user.role === "admin" && <span className="text-[#FF3B30]">· AMMINISTRATORE</span>}
          </div>
          <div className="font-display text-4xl sm:text-5xl font-black uppercase leading-none tracking-tight">
            {user.name}
          </div>
          <div className="text-[#A0A0AB] text-sm mt-1">{user.email}</div>
          <div className="flex items-center gap-3 mt-2">
            <span className={`text-[10px] uppercase tracking-widest flex items-center gap-1 ${
              user.status === "suspended" ? "text-[#EF4444]" : "text-[#22C55E]"
            }`}>
              <Circle className="w-2 h-2 fill-current" />
              {user.status === "suspended" ? "SOSPESO" : "ATTIVO"}
            </span>
            {user.subscription_until && (
              <span className="text-[10px] uppercase tracking-widest text-[#E2FF31]">
                Abb. fino a {user.subscription_until}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={suspend} disabled={saving} data-testid="btn-suspend" className="border border-[#2A2A35] hover:border-[#EF4444] hover:text-[#EF4444] text-xs font-bold uppercase tracking-widest px-3 py-2 flex items-center gap-1">
            <Ban className="w-3 h-3" /> {user.status === "suspended" ? "Riattiva" : "Sospendi"}
          </button>
          <button onClick={del} data-testid="btn-delete" className="border border-[#2A2A35] hover:border-[#EF4444] hover:text-[#EF4444] text-xs font-bold uppercase tracking-widest px-3 py-2 flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Elimina
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#2A2A35] mb-6 overflow-x-auto">
        {[
          ["overview", "Panoramica"],
          ["profile", "Profilo"],
          ["measurements", `Misure (${measurements.length})`],
          ["sessions", `Sessioni (${sessions.length})`],
          ["programs", `Programmi (${programs.length})`],
          ["admin", "Note admin"],
          ["messages", "Messaggi"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`px-4 py-3 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all ${
              tab === k ? "text-[#FF3B30] border-b-2 border-[#FF3B30]" : "text-[#A0A0AB] hover:text-white"
            }`}
          >{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
          <Stat label="Sessioni" value={stats.total_sessions} c="#FF3B30" />
          <Stat label="Minuti tot" value={stats.total_minutes} c="#E2FF31" />
          <Stat label="Kcal tot" value={stats.total_calories} c="#FFFFFF" />
          <Stat label="Peso" value={profile?.weight_kg || "—"} c="#A0A0AB" suffix="kg" />
        </div>
      )}

      {tab === "profile" && (
        <div className="border border-[#2A2A35] p-6">
          {profile ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Info label="Età" v={profile.age} />
              <Info label="Sesso" v={profile.sex} />
              <Info label="Altezza" v={profile.height_cm && `${profile.height_cm} cm`} />
              <Info label="Peso attuale" v={profile.weight_kg && `${profile.weight_kg} kg`} />
              <Info label="Peso obiettivo" v={profile.weight_goal_kg && `${profile.weight_goal_kg} kg`} />
              <Info label="Livello" v={profile.level} />
              <Info label="Sessioni/sett" v={profile.sessions_per_week} />
              <Info label="Durata sess" v={profile.session_duration_min && `${profile.session_duration_min} min`} />
              <Info label="Attrezzatura" v={(profile.equipment || []).join(", ")} />
              <Info label="Obiettivi" v={(profile.goals || []).join(", ")} />
              <Info label="Infortuni" v={profile.injuries} />
              <Info label="Note" v={profile.notes} />
            </div>
          ) : (
            <div className="text-[#A0A0AB] text-sm">Profilo non ancora compilato.</div>
          )}
        </div>
      )}

      {tab === "measurements" && (
        <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
          {measurements.length === 0 ? (
            <div className="p-6 text-[#A0A0AB] text-sm">Nessuna misurazione registrata.</div>
          ) : measurements.slice().reverse().map((m) => (
            <div key={m.id} className="p-3 flex items-center justify-between text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{m.date}</div>
                <div className="font-mono-alt text-xs flex flex-wrap gap-x-3 mt-1">
                  {m.weight_kg && <span>P: <b>{m.weight_kg}</b>kg</span>}
                  {m.waist_cm && <span>Vita: <b>{m.waist_cm}</b>cm</span>}
                  {m.hips_cm && <span>Fianchi: <b>{m.hips_cm}</b>cm</span>}
                  {m.chest_cm && <span>Petto: <b>{m.chest_cm}</b>cm</span>}
                  {m.arm_cm && <span>Braccio: <b>{m.arm_cm}</b>cm</span>}
                  {m.thigh_cm && <span>Coscia: <b>{m.thigh_cm}</b>cm</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "sessions" && (
        <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
          {sessions.length === 0 ? (
            <div className="p-6 text-[#A0A0AB] text-sm">Nessuna sessione.</div>
          ) : sessions.slice(0, 50).map((s) => (
            <div key={s.id} className="p-3 flex items-center justify-between gap-4">
              <div>
                <div className="font-display text-sm font-bold uppercase">{s.workout_title}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{new Date(s.completed_at).toLocaleString("it-IT")}</div>
              </div>
              <div className="text-right">
                <div className="font-mono-alt text-sm text-[#FF3B30]">{Math.round(s.duration_seconds / 60)}min</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{s.calories_estimate}kcal</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "programs" && (
        <div>
          <button onClick={genPlan} disabled={genLoading} data-testid="admin-gen-plan" className="mb-4 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-[#e02e24] transition-all flex items-center gap-2 disabled:opacity-40">
            <Sparkles className="w-3 h-3" /> {genLoading ? "Genero..." : "Genera nuovo programma IA"}
          </button>
          {programs.length === 0 ? (
            <div className="border border-[#2A2A35] p-6 text-[#A0A0AB] text-sm">Nessun programma.</div>
          ) : (
            <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
              {programs.map((p) => (
                <div key={p.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-display text-lg font-bold uppercase flex items-center gap-2">
                      {p.title}
                      {p.active && <span className="text-[9px] uppercase tracking-widest text-[#E2FF31] border border-[#E2FF31] px-1.5">ATTIVO</span>}
                    </div>
                    <div className="text-xs text-[#A0A0AB]">
                      {new Date(p.created_at).toLocaleDateString("it-IT")} · {p.sessions_per_week} sess/sett · {p.weeks} sett
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "admin" && (
        <AdminNoteTab user={user} onSave={save} saving={saving} />
      )}

      {tab === "messages" && (
        <div className="border border-[#2A2A35] bg-[#141419]/50">
          <div className="border-b border-[#2A2A35] px-4 py-2 flex items-center gap-2 text-xs">
            <MessageSquare className="w-3 h-3 text-[#E2FF31]" />
            <span className="uppercase tracking-widest text-[#A0A0AB]">Thread con {user.name}</span>
          </div>
          <div ref={listRef} className="max-h-[500px] min-h-[300px] overflow-y-auto p-4 space-y-3" data-testid="admin-thread">
            {msgs.length === 0 && <div className="text-[#A0A0AB] text-sm">Nessun messaggio.</div>}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.from_admin ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 text-sm ${
                  m.from_admin ? "bg-[#FF3B30] text-white" : "bg-[#2A2A35] text-white"
                }`}>
                  <div>{m.content}</div>
                  <div className="text-[9px] uppercase tracking-widest opacity-60 mt-1">
                    {m.from_admin ? "Admin" : user.name} · {new Date(m.created_at).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#2A2A35] p-3 flex gap-2">
            <input
              value={msgInput}
              onChange={(e) => setMsgInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMsg()}
              data-testid="admin-msg-input"
              placeholder="Scrivi al cliente..."
              className="flex-1 bg-[#0B0B0F] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none text-sm"
            />
            <button onClick={sendMsg} data-testid="admin-msg-send" className="bg-[#FF3B30] text-white px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-[#e02e24]">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminNoteTab({ user, onSave, saving }) {
  const [note, setNote] = useState(user.admin_note || "");
  const [sub, setSub] = useState(user.subscription_until || "");
  const [resetLink, setResetLink] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);

  const genReset = async () => {
    setResetLoading(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/reset-token`);
      setResetLink({
        url: r.data.reset_url.startsWith("http") ? r.data.reset_url : `${window.location.origin}${r.data.reset_url}`,
        token: r.data.token,
        expires_at: r.data.expires_at,
        email_sent: r.data.email_sent,
        email_enabled: r.data.email_enabled,
      });
      if (r.data.email_sent) {
        toast.success(`Email di reset inviata a ${user.email}`);
      } else if (r.data.email_enabled) {
        toast.info("Link generato — invio email fallito, condividilo manualmente");
      } else {
        toast.success("Link generato — condividilo con il cliente");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setResetLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copiato"),
      () => toast.error("Impossibile copiare")
    );
  };

  return (
    <div className="space-y-6">
      <div className="border border-[#2A2A35] p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Nota interna sul cliente</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            data-testid="admin-note-input"
            className="w-full bg-[#141419] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none text-sm"
            placeholder="Es. Preferisce allenamenti al mattino. Sensibile alla schiena..."
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Abbonamento fino a</div>
          <input
            type="date"
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            data-testid="admin-sub-input"
            className="bg-[#141419] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none font-mono-alt text-sm"
          />
        </div>
        <button
          onClick={() => onSave({ admin_note: note, subscription_until: sub || null })}
          disabled={saving}
          data-testid="admin-note-save"
          className="bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-[#e02e24] flex items-center gap-2 disabled:opacity-40"
        >
          <Save className="w-3 h-3" /> {saving ? "Salvo..." : "Salva"}
        </button>
      </div>

      <div className="border border-[#2A2A35] p-6">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-[#E2FF31]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Reset password cliente</div>
        </div>
        <p className="text-xs text-[#A0A0AB] mb-4 max-w-2xl">
          Genera un link di reset one-shot (valido 24h) da condividere manualmente col cliente
          (chat interna, WhatsApp, SMS). Ogni link può essere usato una sola volta.
        </p>
        <button
          onClick={genReset}
          disabled={resetLoading}
          data-testid="gen-reset-btn"
          className="bg-[#E2FF31] text-black text-xs font-bold uppercase tracking-widest px-4 py-2 hover:brightness-110 flex items-center gap-2 disabled:opacity-40"
        >
          <KeyRound className="w-3 h-3" /> {resetLoading ? "Genero..." : "Genera link reset"}
        </button>

        {resetLink && (
          <div className="mt-4 border border-[#E2FF31]/40 p-4 bg-[#141419]" data-testid="reset-link-box">
            <div className="text-[9px] uppercase tracking-widest text-[#E2FF31] mb-2 flex items-center gap-2">
              <span>Link generato · scade {new Date(resetLink.expires_at).toLocaleString("it-IT")}</span>
              {resetLink.email_sent && (
                <span className="text-[#22C55E] flex items-center gap-1" data-testid="email-sent-badge">
                  ✉ EMAIL INVIATA
                </span>
              )}
              {!resetLink.email_sent && resetLink.email_enabled && (
                <span className="text-[#F59E0B]">✗ EMAIL FALLITA</span>
              )}
            </div>
            <div className="flex items-center gap-2 mb-2">
              <input
                readOnly
                value={resetLink.url}
                data-testid="reset-link-url"
                className="flex-1 bg-[#0B0B0F] border border-[#2A2A35] px-3 py-2 font-mono-alt text-xs"
              />
              <button
                onClick={() => copy(resetLink.url)}
                data-testid="copy-url-btn"
                className="border border-[#2A2A35] hover:border-white p-2"
                title="Copia URL"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mb-1">Solo codice:</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={resetLink.token}
                data-testid="reset-link-token"
                className="flex-1 bg-[#0B0B0F] border border-[#2A2A35] px-3 py-2 font-mono-alt text-xs"
              />
              <button
                onClick={() => copy(resetLink.token)}
                data-testid="copy-token-btn"
                className="border border-[#2A2A35] hover:border-white p-2"
                title="Copia codice"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, c, suffix }) {
  return (
    <div className="border border-[#2A2A35] p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">{label}</div>
      <div className="font-display text-4xl font-black tracking-tighter" style={{ color: c }}>{value}</div>
      {suffix && <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-1">{suffix}</div>}
    </div>
  );
}

function Info({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mb-1">{label}</div>
      <div className="text-white">{v || <span className="text-[#A0A0AB]">—</span>}</div>
    </div>
  );
}
